import { isNotNullish, isNullish } from '@nx/stdlib';
import { setFolderPendingRename } from '@query/redux/saved-cypher-slice';
import {
  type Item,
  type ItemDocument,
  type ItemDocumentVersions,
  type PartialItem,
  type SavedCypherTree,
  type SavedCypherTreeDocument,
  type SavedCypherTreeDocumentVersions,
  itemDocumentType,
  savedCypherTreeDocumentType,
} from '@query/services/api';
import { log } from '@query/services/logging';
import { NEW_FOLDER_PREFIX } from '@query/types/saved-cypher';
import { nanoid } from '@reduxjs/toolkit';

import { baseApi } from './base-api';
import type * as types from './saved-cypher-api-types';
import { mergeOrderedItems, removeItemIdsFromSavedCypherTree } from './saved-cypher-api-utils';
import { makeTag } from './types';

const BASE_URL = '/v1/query/saved-cypher-trees';
const SAVED_CYPHER_TREES_LIST_ID = 'SAVED_CYPHER_TREES';
const ITEMS_LIST_ID = 'ITEMS';

/**
 * This API manages Saved Cypher Trees and their Items.
 *
 * A Saved Cypher Tree document stores a hierarchical representation of saved queries, folders and their parents
 * as an array of ordered IDs relating to each Item.
 *
 * Each ID in the tree may be a folder or a query, and the tree structure allows for nesting up to a max depth of 7.
 *
 * Saved Cypher Trees cannot be created or deleted directly through the UI, and are limited to 1 per user.
 * This may be expanded in the future to accommodate a tree per instance or database.
 *
 * Item documents themselves are stored as a flat sub collection of Saved Cypher Trees.
 *
 * The tree view in the UI is constructed by first fetching the Saved Cypher Tree, then fetching the Items, mapping them
 * to their IDs.
 *
 * Whenever new Items are created, moved or deleted, the Saved Cypher Tree must be updated accordingly
 * to reflect the new layout.
 *
 * This has the drawback of requiring multiple API calls to fetch the tree and its items, and to keep them in sync.
 *
 * Therefore, mutations should **not** be consumed directly, but rather through the common hooks
 * exposed from the `@query/services/api/hooks` package which encapsulates the necessary logic for managing
 * the Saved Cypher Trees and their Items.
 *
 * However, it allows for an efficient way to manage a large hierarchical structure of saved queries and folders.
 */
const savedCypherApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Saved Cypher Trees

    /**
     * Fetches all Saved Cypher Trees.
     */
    getSavedCypherTrees: builder.query<SavedCypherTree[], { projectId: string }>({
      query: () => ({
        url: BASE_URL,
        method: 'GET',
      }),
      transformResponse: (response: { items: SavedCypherTreeDocumentVersions[] }) => response.items.map((item) => item),
      providesTags: (res, _, { projectId }) => [
        makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
        ...(res ? res.map(({ id }) => makeTag('SavedCypherTree', projectId, id)) : []),
      ],
    }),

    /**
     * Fetches a Saved Cypher Tree by its ID.
     */
    getSavedCypherTreeById: builder.query<SavedCypherTree, types.GetSavedCypherTreeByIdApiArg>({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.id}`,
        method: 'GET',
      }),
      providesTags: (res, _, { projectId }) => [...(res ? [makeTag('SavedCypherTree', projectId, res.id)] : [])],
      onQueryStarted: async ({ id, projectId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          log.error('[savedCypherApi] getSavedCypherTreeById onQueryStarted: error', error);
          dispatch(
            savedCypherApi.util.invalidateTags([
              makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
              makeTag('Item', id, projectId, ITEMS_LIST_ID),
            ]),
          );
        }
      },
    }),

    /**
     * Creates a new Saved Cypher Tree.
     *
     * Unlike the other mutations, this _does_ invalidate caches by default.
     */
    createSavedCypherTree: builder.mutation<SavedCypherTreeDocument, types.CreateSavedCypherTreeApiArg>({
      query: (queryArg) => ({
        url: BASE_URL,
        method: 'POST',
        body: { ...queryArg.savedCypherTree, version: savedCypherTreeDocumentType.currentVersion },
      }),
      invalidatesTags: (res, _, { projectId }) => [
        makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
        makeTag('SavedCypherTree', projectId, res?.id ?? ''),
        makeTag('Item', projectId, res?.id ?? '', ITEMS_LIST_ID),
      ],
    }),

    /**
     * Updates a Saved Cypher Tree.
     *
     * The following operations are performed in the `onQueryStarted` callback:
     * - Create a temporary Saved Cypher Tree document with a unique ID and current timestamp.
     * - Optimistically updates the caches for `getSavedCypherTrees` and `getSavedCypherTreeById`
     *   with the temporary Saved Cypher Tree in order to update the UI immediately.
     * - If an error occurs, the caches are invalidated to ensure the UI returns to the correct state.
     *
     * For efficiency, the cache is NOT invalidated once the query is fulfilled.
     * Invalidating the cache causes a cascade of refetches, and updating the cache in place is sufficient.
     */
    updateSavedCypherTree: builder.mutation<SavedCypherTreeDocument, types.UpdateSavedCypherTreeApiArg>({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.id}`,
        method: 'PATCH',
        body: { ...queryArg.savedCypherTree, version: savedCypherTreeDocumentType.currentVersion },
      }),
      onQueryStarted: async ({ projectId, id, savedCypherTree }, { dispatch, queryFulfilled }) => {
        const createdAt = new Date().toISOString();
        const tempSavedCypherTree: SavedCypherTreeDocument = {
          ...savedCypherTree,
          id,
          createdAt,
          updatedAt: createdAt,
          version: savedCypherTreeDocumentType.currentVersion,
        };

        dispatch(
          savedCypherApi.util.updateQueryData('getSavedCypherTrees', { projectId }, (draft) => {
            const index = draft.findIndex((tree) => tree.id === id);
            if (index !== -1) {
              draft[index] = tempSavedCypherTree;
            } else {
              draft.push(tempSavedCypherTree);
            }
          }),
        );

        dispatch(
          savedCypherApi.util.updateQueryData('getSavedCypherTreeById', { projectId, id }, (draft) => {
            Object.assign(draft, tempSavedCypherTree);
          }),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          log.error('[savedCypherApi] updateItem onQueryStarted: error', error);
          dispatch(
            savedCypherApi.util.invalidateTags([
              makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
              makeTag('SavedCypherTree', projectId, id),
              makeTag('Item', projectId, id, ITEMS_LIST_ID),
            ]),
          );
        }
      },
    }),

    // Items

    /**
     * Fetches all Items for a Saved Cypher Tree.
     */
    getItems: builder.query<Item[], types.GetItemsApiArg>({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.savedCypherTreeId}/items`,
      }),
      transformResponse: (res: { items: ItemDocumentVersions[] }) => res.items.map((item) => item),
      providesTags: (_, __, { projectId, savedCypherTreeId }) => [
        makeTag('Item', projectId, savedCypherTreeId, ITEMS_LIST_ID),
      ],
      onQueryStarted: async ({ projectId, savedCypherTreeId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          log.error('[savedCypherApi] getItems onQueryStarted: error', error);
          dispatch(savedCypherApi.util.invalidateTags([makeTag('Item', projectId, savedCypherTreeId, ITEMS_LIST_ID)]));
        }
      },
    }),

    /**
     * Creates Items for a Saved Cypher Tree.
     *
     * The following operations are performed in the `onQueryStarted` callback:
     * - Creates temporary Item documents with unique IDs and current timestamps.
     * - If only a single item is being added, and it is a folder, dispatch an action to set the folder pending
     *   rename state with the temporary ID.
     * - Optimistically updates the caches for `getItems` and `getSavedCypherTrees` with the temporary Items,
     *   in order to update the UI immediately.
     * - Once the mutation resolves, the caches are updated with the details of the created Items.
     * - If an error occurs, the caches are invalidated to ensure the UI returns to the correct state.
     *
     * For efficiency, the cache is NOT invalidated once the query is fulfilled.
     * Invalidating the cache causes a cascade of refetches, and updating the cache in place is sufficient.
     */
    batchCreateItems: builder.mutation<ItemDocumentVersions[], types.BatchCreateItemsApiArg>({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.savedCypherTreeId}/items:batchCreate`,
        method: 'POST',
        body: { items: queryArg.items.map((item) => ({ ...item, version: itemDocumentType.currentVersion })) },
      }),
      transformResponse: (res: { items: ItemDocumentVersions[] }) => res.items.map((item) => item),
      onQueryStarted: async ({ projectId, items, savedCypherTreeId }, { dispatch, queryFulfilled }) => {
        const createdAt = new Date().toISOString();
        const tempIds = new Set();
        const tempItemDocuments: ItemDocument[] = items.map((item) => {
          const tempItemId = nanoid();
          tempIds.add(tempItemId);
          return {
            ...item,
            id: tempItemId,
            createdAt,
            updatedAt: createdAt,
            version: itemDocumentType.currentVersion,
          };
        });

        const [tempItem] = tempItemDocuments;

        if (
          tempItemDocuments.length === 1 &&
          isNotNullish(tempItem) &&
          isNullish(tempItem.query) &&
          tempItem.name.startsWith(NEW_FOLDER_PREFIX)
        ) {
          dispatch(setFolderPendingRename({ id: tempItem.id }));
        }

        dispatch(
          savedCypherApi.util.updateQueryData('getItems', { projectId, savedCypherTreeId }, (draft) => {
            draft.push(...tempItemDocuments);
          }),
        );

        const updateTreeCache = (newItems: PartialItem[], createdItems: ItemDocument[]) => {
          dispatch(
            savedCypherApi.util.updateQueryData('getSavedCypherTrees', { projectId }, (draft) => {
              const [tree] = draft;
              if (isNullish(tree)) {
                draft.push({
                  id: nanoid(),
                  createdAt,
                  updatedAt: createdAt,
                  orderedItems: createdItems.map((document) => ({ id: document.id, children: null })),
                });
              } else {
                const orderedItems = mergeOrderedItems(tree.orderedItems, newItems, createdItems);
                Object.assign(tree, { orderedItems });
              }
            }),
          );
        };

        updateTreeCache(items, tempItemDocuments);

        try {
          const res = await queryFulfilled;
          const createdItems = res.data;

          dispatch(
            savedCypherApi.util.updateQueryData('getItems', { projectId, savedCypherTreeId }, (draft) => {
              draft.splice(0, draft.length, ...draft.filter((item) => !tempIds.has(item.id)));
              draft.push(...createdItems);
            }),
          );

          updateTreeCache(items, createdItems);

          const [item] = createdItems;

          if (
            createdItems.length === 1 &&
            isNotNullish(item) &&
            isNullish(item.query) &&
            item.name.startsWith(NEW_FOLDER_PREFIX)
          ) {
            dispatch(setFolderPendingRename({ id: item.id }));
          }
        } catch (error) {
          log.error('[savedCypherApi] createItem onQueryStarted: error', error);
          dispatch(
            savedCypherApi.util.invalidateTags([
              makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
              makeTag('Item', projectId, savedCypherTreeId, ITEMS_LIST_ID),
            ]),
          );
        }
      },
    }),

    /**
     * Updates existing Items for a Saved Cypher Tree.
     *
     * The following operations are performed in the `onQueryStarted` callback:
     * - Create a temporary Item document with a unique ID and current timestamp.
     * - Optimistically updates the cache for `getItems` with the temporary Item, in order to update the UI immediately.
     * - If an error occurs, the caches are invalidated to ensure the UI returns to the correct state.
     *
     * For efficiency, the cache is NOT invalidated once the query is fulfilled.
     * Invalidating the cache causes a cascade of refetches, and updating the cache in place is sufficient.
     */
    batchUpdateItems: builder.mutation<ItemDocumentVersions[], types.BatchUpdateItemsApiArg>({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.savedCypherTreeId}/items:batchUpdate`,
        method: 'POST',
        body: { items: queryArg.items },
      }),
      transformResponse: (res: { items: ItemDocumentVersions[] }) => res.items.map((item) => item),
      onQueryStarted: async ({ projectId, items, savedCypherTreeId }, { dispatch, queryFulfilled }) => {
        const createdAt = new Date().toISOString();
        const tempItemDocuments: ItemDocument[] = items.map((item) => ({
          ...item,
          createdAt,
          updatedAt: createdAt,
          version: itemDocumentType.currentVersion,
        }));

        dispatch(
          savedCypherApi.util.updateQueryData(
            'getItems',
            { projectId, savedCypherTreeId: savedCypherTreeId },
            (draft) => {
              const tempMap = new Map(tempItemDocuments.map((doc) => [doc.id, doc]));
              for (const item of draft) {
                if (tempMap.has(item.id)) {
                  Object.assign(item, tempMap.get(item.id));
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          log.error('[savedCypherApi] updateItem onQueryStarted: error', error);
          dispatch(
            savedCypherApi.util.invalidateTags([
              makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
              makeTag('SavedCypherTree', projectId, savedCypherTreeId),
              makeTag('Item', projectId, savedCypherTreeId, ITEMS_LIST_ID),
            ]),
          );
        }
      },
    }),

    /**
     * Deletes Items from a Saved Cypher Tree.
     *
     * The following operations are performed in the `onQueryStarted` callback:
     * - Optimistically removes the items from the caches for `getItems` and `getSavedCypherTrees`,
     *   in order to update the UI immediately.
     * - If an error occurs, the caches are invalidated to ensure the UI returns to the correct state.
     *
     * For efficiency, the cache is NOT invalidated once the query is fulfilled.
     * Invalidating the cache causes a cascade of refetches, and updating the cache in place is sufficient.
     */
    batchDeleteItems: builder.mutation<string[], types.BatchDeleteItemsApiArg>({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.savedCypherTreeId}/items:batchDelete`,
        method: 'POST',
        body: [...queryArg.itemIds],
      }),
      transformResponse: (response: types.BatchDeleteResponse) => response.map((item) => item.id),
      onQueryStarted: async ({ projectId, savedCypherTreeId }, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled;
          const ids = res.data;

          dispatch(
            savedCypherApi.util.updateQueryData('getItems', { projectId, savedCypherTreeId }, (draft) => {
              const idSet = new Set(ids);
              draft.splice(0, draft.length, ...draft.filter((item) => !idSet.has(item.id)));
            }),
          );

          dispatch(
            savedCypherApi.util.updateQueryData('getSavedCypherTrees', { projectId }, (draft) => {
              const [tree] = draft;
              if (tree !== undefined) {
                const updatedTree = removeItemIdsFromSavedCypherTree(tree, ids);
                Object.assign(tree, updatedTree);
              }
            }),
          );
        } catch (error) {
          log.error('[savedCypherApi] batchDeleteItems onQueryStarted: error', error);
          dispatch(
            savedCypherApi.util.invalidateTags([
              makeTag('SavedCypherTree', projectId, SAVED_CYPHER_TREES_LIST_ID),
              makeTag('SavedCypherTree', projectId, savedCypherTreeId),
              makeTag('Item', projectId, savedCypherTreeId, ITEMS_LIST_ID),
            ]),
          );
        }
      },
    }),
  }),
  overrideExisting: false,
});

export { savedCypherApi };

export const {
  useGetSavedCypherTreesQuery,
  useLazyGetSavedCypherTreeByIdQuery,
  useCreateSavedCypherTreeMutation,
  useUpdateSavedCypherTreeMutation,
  useGetItemsQuery: useGetSavedCypherItemsQuery,
  useBatchCreateItemsMutation: useBatchCreateSavedCypherItemsMutation,
  useBatchUpdateItemsMutation: useBatchUpdateSavedCypherItemsMutation,
  useBatchDeleteItemsMutation: useBatchDeleteSavedCypherItemsMutation,
} = savedCypherApi;
